<template>
  <app-modal :loading="loading" @click-outside="onClose">
    <div class="modal-content">
      <div class="modal-title-bar">
        <span class="modal-title">
          <fa-icon-compat class="ai-icon" icon="fa-solid fa-sparkles" />
          {{ $t('modal.inspirationModal.title') }}
        </span>

        <app-icon-button :size="32" @click="onClose">
          <fa-icon-compat icon="fa-regular fa-xmark" />
        </app-icon-button>
      </div>

      <template v-if="showSkills">
        <p class="description">
          {{ $t('modal.inspirationModal.updateSkills') }}
        </p>

        <profile-skills />

        <app-button
          :label="$t('modal.inspirationModal.changeSkillButton')"
          class="close-skills"
          @click="toggleSkills"
        />
      </template>

      <template v-else>
        <p class="description">
          {{ $t('modal.inspirationModal.description') }}
        </p>

        <div class="suggestion-container">
          <div
            v-for="({ title, description }, index) in suggestions"
            :key="title"
            :title="description"
            class="suggestion-pill"
            @click="useSuggestion(index)"
          >
            {{ title }}
          </div>
        </div>

        <router-link
          :to="toCreateTicket"
          class="skip-suggestions"
          @click.native="onClose"
        >
          {{ $t('modal.inspirationModal.skipSuggestions') }}
        </router-link>
      </template>
    </div>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { RawLocation } from 'vue-router';
import type { ITicketResponse, ITitleSuggestion } from '@rready/sdk';
import { useContextStore } from '../../store/contextStore';
import { useProductStore } from '../../store/productStore';
import { useAuthStore } from '../../store/authStore';
import ProfileSkills from '../profile/ProfileSkills.vue';

export default defineComponent({
  name: 'InspirationModal',
  components: {
    ProfileSkills
  },
  props: {
    parentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      showSkills: false,
      suggestions: [] as ITitleSuggestion[],
      userSkills: [] as string[],
      parentTicket: undefined as undefined | ITicketResponse
    };
  },
  computed: {
    contextId(): string {
      return useContextStore().contextId!;
    },
    currentUserId(): string {
      return useAuthStore().user!.id;
    },
    productId(): string {
      return useProductStore().productId!;
    },
    toCreateTicket(): RawLocation {
      return {
        name: 'CreateTicket',
        query: {
          parent: this.parentId
        },
        hash: ''
      };
    }
  },
  async created(): Promise<void> {
    this.userSkills = await this.$api.skills.fetchCurrentUserSkills();

    if (!this.userSkills.length) {
      this.toggleSkills();
      return;
    }

    await this.loadSuggestions();
  },
  methods: {
    async loadSuggestions(): Promise<void> {
      this.loading = true;
      try {
        const response = await this.$gateway.ticket.suggestTicketTitles(
          this.currentUserId,
          this.contextId,
          this.parentId
        );
        this.suggestions = response?.fields ?? [];
      } catch (e: any) {
        console.error(e);
        this.$toast(this.$t('error.general').toString());
      } finally {
        this.loading = false;
      }
    },
    async useSuggestion(suggestionIndex: number): Promise<void> {
      this.loading = true;
      const generatedTicket = await this.$api.ticket.createAITicket(
        this.productId,
        this.parentId,
        this.suggestions[suggestionIndex]
      );
      this.$router.push({
        name: 'EditTicket',
        params: {
          id: generatedTicket.canonicalId
        }
      });
      this.onClose();
    },
    toggleSkills(): void {
      this.showSkills = !this.showSkills;
      if (!this.showSkills) {
        this.loadSuggestions();
      }
    },
    onClose(): void {
      this.$emit('close');
    }
  }
});
</script>

<style lang="scss" scoped>
::v-deep .app-modal {
  height: fit-content;
  width: 896px !important;
  max-width: min(896px, 100%) !important;
  overflow: unset !important;
  overflow-y: visible !important;
}

.description {
  margin-bottom: 16px;
  font-size: var(--font-size-small);
  color: var(--color-grey-light-4);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 32px;
}

.modal-title-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.ai-icon {
  color: var(--color-blue);
}

.modal-title {
  font-weight: 600;
  font-size: 22px;
  color: var(----color-black);
  flex-grow: 1;
}

.suggestion-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.suggestion-pill {
  padding: 16px 32px;
  border-radius: var(--radius-3);
  background-color: var(--color-blue-light);
  color: var(--color-blue);
  font-weight: 700;
  font-size: var(--font-size-small);
  text-transform: uppercase;
  cursor: pointer;
}

.change-skills {
  cursor: pointer;
  font-size: var(--font-size-small);
  color: var(--color-blue);
}

.skip-suggestions {
  text-align: right;
  color: var(--color-grey-light-4);
  width: 100%;
}
</style>
